/**
 * The case's standing sources (PRD-0075 R-B6): each upload folder and SFTP folder the case's
 * sources keep coming through, how many came, and the last intake. Day folders are the received
 * date, not a source, so a folder that gets a new day every week is one row.
 *
 * Presentational: the feature reads the sources and opens a document in the Documents page.
 */
import {
  Database,
  FileText,
  FolderInput,
  HardDriveUpload,
  Inbox,
  Upload,
  UserRound,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDate } from '@/utils/date'
import { type LoadView, MonitorSection } from './MonitorSection'

const SOURCES = 'cases.monitor.sources'

export const SOURCE_CHANNELS = ['upload', 'sftp', 'lake_import', 'humint'] as const
export type SourceChannel = (typeof SOURCE_CHANNELS)[number]

const CHANNEL_ICONS = {
  upload: Upload,
  sftp: HardDriveUpload,
  lake_import: Database,
  humint: UserRound,
}

export interface StandingSourceRow {
  key: string
  /** Null for a channel this build does not know. */
  channel: SourceChannel | null
  /** "" is the case's root folder. */
  folder: string
  documents: number
  /** ISO 8601. */
  lastReceivedAt: string
  lastDocumentId: string | null
  lastDocumentName: string | null
}

export interface SourcesPaneProps {
  view: LoadView<StandingSourceRow>
  onOpenDocument: (documentId: string) => void
  onRetry: () => void
}

function LastDocument({ row, onOpen }: { row: StandingSourceRow; onOpen: (id: string) => void }) {
  const { t } = useTranslation()
  const { lastDocumentId: id, lastDocumentName: name } = row
  if (!name) return null
  const label = t(`${SOURCES}.lastDocument`, { name })
  if (!id) return <span className="truncate">{label}</span>
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="inline-flex min-w-0 items-center gap-1 truncate underline-offset-2 hover:text-foreground hover:underline"
    >
      <FileText aria-hidden className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  )
}

function SourceRow({
  row,
  onOpenDocument,
}: {
  row: StandingSourceRow
  onOpenDocument: (id: string) => void
}) {
  const { t, i18n } = useTranslation()
  const Icon = row.channel ? CHANNEL_ICONS[row.channel] : FolderInput
  const received = formatDate(row.lastReceivedAt, i18n.language)
  return (
    <li className="flex flex-col gap-1 rounded-md border border-border/60 bg-card px-3 py-2.5 md:flex-row md:items-center md:gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
          <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <span className="shrink-0">{t(`${SOURCES}.channels.${row.channel ?? 'unknown'}`)}</span>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <span className="truncate" title={row.folder || undefined}>
            {row.folder || t(`${SOURCES}.root`)}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <LastDocument row={row} onOpen={onOpenDocument} />
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums">
        <span>{t(`${SOURCES}.documents`, { count: row.documents })}</span>
        <time
          dateTime={row.lastReceivedAt}
          title={t(`${SOURCES}.lastReceived`, { date: received })}
        >
          {received}
        </time>
      </div>
    </li>
  )
}

export function SourcesPane({ view, onOpenDocument, onRetry }: SourcesPaneProps) {
  const { t } = useTranslation()
  return (
    <MonitorSection
      heading={{ id: 'monitor-sources-title', icon: Inbox, title: t(`${SOURCES}.title`) }}
      view={view}
      copy={{
        loading: t(`${SOURCES}.loading`),
        failed: t(`${SOURCES}.loadError`),
        empty: t(`${SOURCES}.empty`),
      }}
      onRetry={onRetry}
    >
      {rows => (
        <ul className="flex flex-col gap-2">
          {rows.map(row => (
            <SourceRow key={row.key} row={row} onOpenDocument={onOpenDocument} />
          ))}
        </ul>
      )}
    </MonitorSection>
  )
}
